import { requestHttp } from '@/utils';

const SERVER = ``;

/**
 * 请求更新方案分享信息
 *
 * @author 黄冰洋
 * @see https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/updateShareInfoUsingPOST
 *
 * @param { object } params 请求参数
 * @param { string } params.schemeId 方案ID
 * @param { boolean } params.shareOpen 方案分享权限是否开启
 * @param { 0 | 1 | 2 | 3 } [params.shareTimeType] 分享方案有效期
 * - `0` - 永久
 * - `1` - 一天
 * - `2` - 一周
 * - `3` - 一个月
 * @param { 2 | 3 } [params.shareType] 分享权限
 * - `2` - 接收方案的人仅预览
 * - `3` - 接收方案的人可编辑
 * @param { object } [query] URL 请求参数
 */
export const requestUpdateSchemeShare = async (params, query = {}) => {
  const result = await requestHttp({
    api: `${SERVER}/share/update`,
    method: 'POST',
    module: 'schemes',
    params,
    query
  });

  if (result.code === 0) {
    return result.data;
  } else {
    return Promise.reject(result);
  }
};

/**
 * 方案分享详情类型定义
 *
 * **接口文档** - 黄冰洋<huangbingyang@lysoratech.com>
 *
 * - [查看方案分享详情](https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/getShareInfoUsingGET) -https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/getShareInfoUsingGET
 *
 * - [更新方案分享信息](https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/updateShareInfoUsingPOST) - https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/updateShareInfoUsingPOST
 *
 * @typedef { object } SchemeShareDetail
 * @property { string } schemeId 方案ID
 * @property { string } schemeName 方案名称
 * @property { string } shareExpiredAt 分享有效期
 * @property { boolean } shareOpen 分享权限是否开启
 * @property { 0 | 1 | 2 | 3 } shareTimeType 分享有效期
 * - `0` - 永久
 * - `1` - 一天
 * - `2` - 一周
 * - `3` - 一个月
 * @property { 0 | 2 | 3 } shareType 分享权限
 * - `2` - 接收方案的人仅预览
 * - `3` - 接收方案的人可编辑
 * @property { array } receiverAccountList 分享接收人列表
 */

/**
 * 请求方案分享详情
 *
 * **接口文档** - 黄冰洋<huangbingyang@lysoratech.com>
 *
 * - [查看方案分享详情](https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/getShareInfoUsingGET) -https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/getShareInfoUsingGET
 *
 * @param {object} params 请求参数
 * @param { string } params.schemeId 方案ID
 * @returns { Promise<SchemeShareDetail> } 返回分享详情
 */
export const requestSchemeShareDetail = async params => {
  const result = await requestHttp({
    api: `${SERVER}/share/info`,
    method: 'GET',
    module: 'schemes',
    querys: params
  });

  if (result.code === 0) {
    return result.data;
  } else {
    return Promise.reject(result);
  }
};

/**
 * 请求方案分享码
 *
 * **接口文档**
 * @author 黄冰洋
 * @see https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/createShareCodeUsingPOST
 *
 * @param { object } params 请求参数
 * @param { string } params.schemeId 方案ID
 * @returns { Promise<{ shareCode: string }> } 返回分享码
 */
export const requestSchemeShareCode = async params => {
  const result = await requestHttp({
    api: `${SERVER}/share/code`,
    method: 'POST',
    module: 'schemes',
    params
  });

  if (result.code === 0) {
    return result.data;
  } else {
    return Promise.reject(result);
  }
};

/**
 * 方案分享接收结果类型定义
 *
 * @typedef { Object } SchemeShareReceiveResult
 * @property { boolean } ownScheme 是否本人方案
 * @property { boolean } received 是否已接收
 * @property { string } schemeId 方案ID
 */

/**
 * 请求接收方案分享
 *
 * **接口文档**
 * @author 黄冰洋
 * @see https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/receiveSchemeUsingPOST
 *
 * @param { object } params 请求参数
 * @param { string } params.shareCode 分享码
 * @returns { Promise<SchemeShareReceiveResult> } 返回方案分享接收结果
 */
export const requestReceiveSchemeShare = async params => {
  const result = await requestHttp({
    api: `${SERVER}/share/receive`,
    method: 'POST',
    module: 'schemes',
    params
  });

  if (result.code === 0) {
    return result.data;
  } else {
    return Promise.reject(result);
  }
};

/**
 * 请求移除已接收的方案分享
 *
 * **接口文档**
 * @author 黄冰洋
 * @see https://lysoratech-enet-test.rj.link:6443/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88%E5%88%86%E4%BA%AB/stopReceiveSchemeUsingPOST
 *
 * @param { object } params 请求参数
 * @param { string } params.schemeId 方案ID
 * @returns { Promise } 返回取消Promise
 */
export const requestDeleteReceivedScheme = async params => {
  const result = await requestHttp({
    api: `${SERVER}/share/stop/receive`,
    method: 'POST',
    module: 'schemes',
    params
  });

  if (result.code === 0) {
    return result.data;
  } else {
    return Promise.reject(result);
  }
};
